import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import cn from 'classnames'
import React, { ComponentPropsWithRef, forwardRef } from 'react'
import styled from 'styled-components'

type ElementPosition = 'left' | 'right'

import useText from '../hooks/useText'

import { SContainer, SLabel, Slider } from './Toggle.styling'

interface IToggleProps extends ComponentPropsWithRef<'button'> {
    disabled?: boolean
    loading?: boolean
    toggleFn?: () => void
    isToggled: boolean
    size?: 'sm' | 'md' | 'lg'
    toggleText?: string
    showStateLabel?: boolean
    icon?: React.ReactElement
    iconPos?: ElementPosition
    labelPos?: ElementPosition
}

const Label = styled.span`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.sizing.margin.m};
`

export const Toggle = forwardRef(function Toggle(
    {
        size = 'md',
        showStateLabel = false,
        loading,
        labelPos = 'left',
        icon,
        iconPos = 'right',
        toggleText,
        isToggled,
        disabled,
        toggleFn,
        ...props
    }: IToggleProps,
    ref
): React.ReactElement {
    const text = useText()
    const renderStateLabel = isToggled ? text('lblOn') : text('lblOff')

    const label = (showStateLabel || toggleText) && (
        <Label data-testid={`${props['data-testid']}-toggleText`}>
            {iconPos === 'left' && icon}
            {toggleText ?? renderStateLabel}
            {iconPos === 'right' && icon}
        </Label>
    )

    return (
        <SContainer onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()} {...props}>
            {labelPos === 'left' && label}
            <SLabel
                className={cn({ toggled: isToggled, disabled: disabled })}
                size={size}
                data-testid={`${props['data-testid']}-label`}
            >
                {loading ? (
                    <FontAwesomeIcon icon={faSpinner} pulse data-testid="spinner" />
                ) : (
                    <Slider
                        ref={ref}
                        id="slider"
                        onClick={() => {
                            if (!disabled && toggleFn) toggleFn()
                        }}
                        className={cn({ disabled: disabled })}
                        data-testid={`${props['data-testid']}-slider`}
                    />
                )}
            </SLabel>
            {labelPos === 'right' && label}
        </SContainer>
    )
})

export default Toggle
